import Scroll from "components/shared/scroll";
//import Switchable from "components/Switchable";
//import Content from "pages/template/content";

import Button from "components/shared/button";
import Loader from "components/shared/loader";
import StickyElement from "components/shared/stickystuck";
import { Text } from "components/shared/text";
import { usePopup } from "providers/PopupProvider";
import React, {
  Fragment,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { roundTo1Decimals } from "utils";
import { formatPrice } from "utils/helpers";
import { SystemCode } from "utils/engine/consts";
import { useAuth } from "utils/hooks/useAuth";
import useTranslation from "utils/hooks/useTranslation";
import { SavePopup } from "../../popups/SavePopup";
import { useCurrentEtudeData } from "../hooks/useCurrentEtudeData";
import { useEtudeUpdate } from "../hooks/useEtudeUpdate";
import { useGetSolutions } from "../hooks/useGetSolutions";
import { InstallationType } from "../hooks/useGetSolutionsQuery";
import {
  ActionBlockItemTop,
  BallonDeChargeBlock,
  ElelctricAppontBlock,
  Grid,
  RowCaptionBlock,
  ValueBlock,
} from "./GridComponents";
import { EmptyContent } from "./GridComponents/EmptyContent";
import { RowWithLeftTitle } from "./GridComponents/RowWithLeftTitle";
import {
  getSolutionID,
  getSolutionNomDeProposition,
} from "./SolutionDetails/helpers";
import { SolutionFilters } from "./SolutionFilters";
import { AppliedFilters } from "./SolutionFilters/AppliedFilters";
import { EtudeStep } from "utils/defines";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";

export type LocationState = {
  solutions: Solution[];
  type?: InstallationType;
  appliedSolutionFilter?: GetSolutionRequest | null;
  etudeData?: any;
  service?: string;
  solution?: Solution;
};

export enum ServiceNames {
  ECS = "ecs",
  Chauffage = "chauffage",
  DoubleService = "chauffage-ecs",
}

export const Solutions: React.FC = () => {
  const { t } = useTranslation();
  const { isSolutionsFetching } = useGetSolutions();
  const { isAuth } = useAuth();
  const { openPopup, closeLastPopup } = usePopup();
  const [isStuck, setIsStuck] = useState(false);
  const { network } = useSalesNetwork();

  const setIsStuckCallback = useCallback((entry: IntersectionObserverEntry) => {
    setIsStuck(entry.intersectionRatio < 1);
  }, []);

  const {
    activeSolutions: solutions,
    installationType,
    solutionRequestBody,
    json,
    service,
    isLoading,
    hasEcs,
    hasChauffage,
    hasFXBallon,
  } = useCurrentEtudeData();

  const hasTcoupure = !!(json?.TcoupureQuestion && json?.Tcoupure != null);

  useEffect(() => {
    return () => {
      (window as any).wasInSolutionsPage = true;
    };
  }, []);

  const jsonToSave = {
    ...json,
    step: EtudeStep.Solutions,
    stepName: t("FO_STUDIES_IN_PROGRESS"),
  };

  const { handleUpdateJson, isLoadingEtudeJsonSave } = useEtudeUpdate(
    undefined,
    jsonToSave
  );

  const Row = useMemo(
    () => (props: Omit<SolutionRowProps, "solutions">) => {
      return (
        <SolutionRow {...(props as SolutionRowProps)} solutions={solutions} />
      );
    },
    [solutions, t]
  );

  const withFxBallonDash = useCallback(
    (solution: Solution, getValue: () => string) => {
      if (hasFXBallon(solution)) {
        return "-";
      }

      return getValue();
    },
    [hasFXBallon]
  );

  const isFxBallon = useCallback(
    (ballon: Ballon | undefined | null) => ballon?.code === "FXBALLON",
    []
  );

  const topActionBlocksRow = (<group
    data-wrap="no"
    data-sticky-row=""
    data-sticky="top"
    data-direction="column"
    style={{ zIndex: 104 }
    }
  >
    <Row
      captionBlock={<AppliedFilters />}
      render={(s) => <ActionBlockItemTop solution={s} />}
    />
    {network?.showPrice && <>
      <Row
        caption="FO_RESULT_PAC_PRICE"
        textSize={22}
        getValue={(s) => {
          const pacPrice = s.panier
            .filter((item) => item.tag === "HP")
            .reduce((sum, item) => sum + item.prix * (item.quantite ?? 1), 0);
          return `${formatPrice(pacPrice)} ${t("FO_SOL_CURRENCY")}`;
        }}
      />
      <Row
        caption="FO_RESULT_TOTAL_PRICE"
        valueHelpText="FO_WARNING_EXE_SPE"
        isValueWarning={hasFXBallon}
        getValue={(s) => hasFXBallon(s) ? t("FO_EXE_CONSULTER") : `${formatPrice(s.prix)} ${t("FO_SOL_CURRENCY")}`}
        textSize={22}
      />
    </>
    }
  </group >
  );

  const generalInfoGridRows = useMemo(() => {
    let result: ReactElement | null = null;

    const pacNameRow = (
      <Row
        captionBlock={null}
        caption="FO_RESULT_PAC_MODEL"
        isTitle
        getValue={(s) => getSolutionNomDeProposition(s)}
      />
    );
    const ballonDeCharge1Row = (
      <Row
        caption="FO_RESULT_BUFFER_#1"
        caption2={
          installationType === InstallationType.electric
            ? "FO_RESULT_ELEC_KITS"
            : ""
        }
        render={(s) => {
          const ballon = (installationType === InstallationType.electric
            ? (s.ballons as ElectricBallons).primaires
            : (s.ballons as Ballon[]))?.[0];

          return isFxBallon(ballon) ? (
            <ValueBlock value={t("FO_SOL_FAUX_BALLON")} />
          ) : (
            <BallonDeChargeBlock
              ballon={ballon}
              showPuissance={installationType === InstallationType.electric}
            />
          );
        }}
      />
    );
    const ballonDeCharge2Row = (
      <Row
        caption="FO_RESULT_BUFFER_#2"
        render={(s) => {
          const ballon = (installationType === InstallationType.electric
            ? (s.ballons as ElectricBallons).primaires
            : (s.ballons as Ballon[]))?.[1];

          return isFxBallon(ballon) ? (
            <ValueBlock value={t("FO_SOL_FAUX_BALLON")} />
          ) : (
            <BallonDeChargeBlock ballon={ballon} />
          );
        }}
      />
    );

    const _ballonECSRows = [
      {
        caption:
          json?.systeme == SystemCode.BALLONS_ECS_PREPARATEURS
            ? "FO_RESULT_PREP_BUFFER"
            : "FO_RESULT_EXCHANGE",
        render: (s: Solution) => (
          <BallonDeChargeBlock ballon={s.ecs?.echangeur as Ballon} />
        ),
      },
      {
        caption: "FO_RESULT_DWH_BUFFER#1",
        render: (s: Solution) => (
          <BallonDeChargeBlock
            ballon={s.ecs?.ballons_stockage_ecs[0] as Ballon}
          />
        ),
      },
      {
        caption: "FO_RESULT_DWH_BUFFER#2",
        render: (s: Solution) => (
          <BallonDeChargeBlock
            ballon={s.ecs?.ballons_stockage_ecs[1] as Ballon}
          />
        ),
      },
    ];
    const _ballonPrimaryRows = [
      {
        caption: "FO_RESULT_BUFFER_#1",
        ...(!(service === ServiceNames.DoubleService && installationType === InstallationType.hybride) && {
          caption2: "FO_RESULT_ELEC_KITS",
        }),
        render: (s: Solution) => {
          const ballon = (installationType === InstallationType.electric
            ? (s.ballons as ElectricBallons).primaires
            : (s.ballons as Ballon[]))?.[0];

          return isFxBallon(ballon) ? (
            <ValueBlock value={t("FO_SOL_FAUX_BALLON")} />
          ) : (
            <BallonDeChargeBlock
              ballon={ballon}
              showPuissance={installationType === InstallationType.electric}
            />
          );
        },
      },
      {
        caption: "FO_RESULT_BUFFER_#2",
        render: (s: Solution) => {
          const ballon = (installationType === InstallationType.electric
            ? (s.ballons as ElectricBallons).primaires
            : (s.ballons as Ballon[]))?.[1];

          return isFxBallon(ballon) ? (
            <ValueBlock value={t("FO_SOL_FAUX_BALLON")} />
          ) : (
            <BallonDeChargeBlock ballon={ballon} />
          );
        },
      },
      ...(!(service === ServiceNames.DoubleService && installationType === InstallationType.hybride) ? [{
        caption: "FO_RESULT_BUFFER_OTHER",
        ...(service === ServiceNames.DoubleService && {
          caption2: "FO_RESULT_ELEC_KITS",
        }),
        render: (s: Solution) => {
          const ballon = (s.ballons as ElectricBallons).secondaires?.[0];

          return isFxBallon(ballon) ? (
            <ValueBlock value={t("FO_SOL_FAUX_BALLON")} />
          ) : (
            <BallonDeChargeBlock
              ballon={ballon}
              showPuissance
            />
          );
        },
      },] : [])
    ];

    switch (installationType) {
      case InstallationType.electric:
        const electricAppointRow = (
          <Row
            caption="FO_RESULT_ELEC_KITS_OUTPUT"
            helpText={t("FO_HELP_ELEC_TOTAL")}
            render={(s) => <ElelctricAppontBlock solution={s} />}
          />
        );
        const ballonSupplimantaireRow = (
          <Row
            caption="FO_RESULT_BUFFER_OTHER"
            caption2="FO_RESULT_ELEC_KITS"
            render={(s) => {
              const ballon = (s.ballons as ElectricBallons).secondaires?.[0];

              return isFxBallon(ballon) ? (
                <ValueBlock value={t("FO_SOL_FAUX_BALLON")} />
              ) : (
                <BallonDeChargeBlock
                  ballon={ballon}
                  showPuissance
                />
              );
            }}
          />
        );
        result = (
          <>
            {pacNameRow}
            {electricAppointRow}
            {(service === ServiceNames.ECS ||
              service === ServiceNames.DoubleService) && (
                <>
                  <RowWithLeftTitle
                    title={t("FO_RESULT_PRIMARY")}
                    rows={_ballonPrimaryRows}
                    solutions={solutions}
                  />
                  <RowWithLeftTitle
                    title={t("FO_RESULT_ECS")}
                    rows={_ballonECSRows}
                    solutions={solutions}
                  />
                </>
              )}
            {service === ServiceNames.Chauffage && (
              <>
                {ballonDeCharge1Row}
                {ballonDeCharge2Row}
                {ballonSupplimantaireRow}
              </>
            )}
          </>
        );

        break;
      case InstallationType.hybride:
        const circuitHybridesRow = (
          <Row
            caption="FO_RESULT_SUPPORTED_CIRCUIT"
            getValue={(s) =>
              `${t(
                (s.circuits_hybrides?.length as number) > 1
                  ? "FO_RESULT_ALL_CIRCUITS"
                  : "FO_RESULT_CIRCUIT1"
              )} ${(s.circuits_hybrides?.length as number) > 1
                ? ""
                : s.circuits_hybrides?.join(" ")
              }`
            }
          />
        );

        result = (
          <>
            {pacNameRow}
            {service === ServiceNames.DoubleService && (
              <>
                <RowWithLeftTitle
                  title={t("FO_RESULT_PRIMARY")}
                  rows={_ballonPrimaryRows}
                  solutions={solutions}
                />
                <RowWithLeftTitle
                  title={t("FO_RESULT_ECS")}
                  rows={_ballonECSRows}
                  solutions={solutions}
                />
              </>
            )}
            {service !== ServiceNames.DoubleService && (
              <>
                {ballonDeCharge1Row}
                {ballonDeCharge2Row}
                {circuitHybridesRow}
              </>
            )}
          </>
        );
        break;
    }

    return result;
  }, [Row, hasFXBallon, installationType, service]);

  const performanceGridRows = useMemo(() => {
    const partPacTbaseRows = (
      <Row
        caption={
          installationType === InstallationType.electric
            ? "FO_RESULT_%_HP_@T°C_BASIS"
            : hasTcoupure
              ? "FO_RESULT_kW_HP_@T°C_COUPURE_TOTAL"
              : "FO_RESULT_kW_HP_@T°C_BASIS_TOTAL"
        }
        helpText={t(hasTcoupure ? "FO_HELP_%_HP_@T°C_COUPURE" : "FO_HELP_%_HP_@T°C_BASIS")}
        getValue={(s) =>
          `${Math.round((hasTcoupure ? (s.part_pac_t_coupure ?? s.part_pac) : s.part_pac) * 100)} ${t("FO_UNIT_PERCENT")}`
        }
      />
    );
    const puissancePacTotaleTbaseRow = (
      <Row
        caption={hasTcoupure ? "FO_RESULT_kW_HP_@T°C_COUPURE" : "FO_RESULT_kW_HP_@T°C_BASIS"}
        getValue={(s) =>
          `${roundTo1Decimals(hasTcoupure ? (s.pac.puissance_a_t_coupure ?? s.pac.puissance_a_t_base) : s.pac.puissance_a_t_base)} ${t("FO_UNIT_KW")}`
        }
      />
    );
    const tauxCouverturePACAnnualEstimeRow = (
      <Row
        caption={
          installationType === InstallationType.electric
            ? "FO_RESULT_%_ANNUAL_COVERAGE"
            : "FO_RESULT_%_ANNUAL_COV_TOTAL"
        }
        helpText={t("FO_HELP_%_ANNUAL_COVERAGE")}
        getValue={(s) =>
          `${Math.round(s.taux_couverture * 100)} ${t("FO_UNIT_PERCENT")}`
        }
      />
    );

    const annualCircuitDSRow = <Row
      caption={"FO_RESULT_%_ANNUAL_COV_CIRCUIT_DS"}
      getValue={(s) =>
        `${Math.round((s.taux_couverture_circuits_pac * 100))} ${t("FO_UNIT_PERCENT")}`
      }
    />;

    const hybridePerformanceRows = (
      <>
        <Row
          caption={
            hasTcoupure
              ? "FO_RESULT_kW_HP_@T°C_COUPURE_TOTAL"
              : "FO_RESULT_%_HP_@T°C_BASIS_HEAT"
          }
          helpText={
            hasTcoupure
              ? t("FO_HELP_%_HP_@T°C_COUPURE")
              : "FO_HELP_%_HP_@_T°C_BASIS_DS"
          }
          getValue={(s) => {
            const dsPartPac = hasTcoupure
              ? s.ecs?.ds_part_pac_a_t_coupure ?? s.ecs?.ds_part_pac_a_t_base
              : s.ecs?.ds_part_pac_a_t_base;

            if (dsPartPac !== undefined && dsPartPac !== null) {
              return `${Math.round(dsPartPac * 100)} ${t("FO_UNIT_PERCENT")}`;
            }
          }}

        />
        <Row
          caption={
            hasTcoupure
              ? "FO_RESULT_%_HP_@T°C_COUPURE_HEAT_DWH"
              : "FO_RESULT_%_HP_@T°C_BASIS_HEAT_DWH"
          }
          getValue={(s) =>
            `${Math.round((hasTcoupure ? (s.part_pac_t_coupure ?? s.part_pac) : s.part_pac) * 100)} ${t("FO_UNIT_PERCENT")}`
          }
        />
        {installationType !== InstallationType.hybride && (
          <Row
            caption={"FO_RESULT_%_HP_@T°C_BASIS_WITH_KIT_HEAT_DWH"}
            getValue={(s) => {
              if (s.ecs?.ds_part_pac_et_appoint_a_t_base) {
                return `${Math.round(
                  s.ecs?.ds_part_pac_et_appoint_a_t_base * 100
                )} ${t("FO_UNIT_PERCENT")}`;
              }
            }}
          />
        )}
        <Row
          caption={hasTcoupure ? "FO_RESULT_kW_HP_@T°C_COUPURE" : "FO_RESULT_kW_HP_@T°C_BASIS"}
          getValue={(s) =>
            `${roundTo1Decimals(hasTcoupure ? (s.pac.puissance_a_t_coupure ?? s.pac.puissance_a_t_base) : s.pac.puissance_a_t_base)} ${t("FO_UNIT_KW")}`
          }
        />
        {tauxCouverturePACAnnualEstimeRow}

      </>
    );

    let result: ReactElement | null = null;
    switch (installationType) {
      case InstallationType.electric:
        result = (
          <>
            {service === ServiceNames.DoubleService ? (
              hybridePerformanceRows
            ) : (
              <>
                {partPacTbaseRows}
                {puissancePacTotaleTbaseRow}
                {service !== ServiceNames.ECS &&
                  tauxCouverturePACAnnualEstimeRow}
              </>
            )}
          </>
        );
        break;
      case InstallationType.hybride:
        const partPacCircuit = (
          <Row
            caption={hasTcoupure ? "FO_RESULT_kW_HP_@T°C_COUPURE_CIRCUIT" : "FO_RESULT_%_HP_@T°C_BASIS_CIRCUIT"}
            helpText={hasTcoupure ? "FO_HELP_%_HP_@T°C_COUPURE_CIRCUIT" : "FO_HELP_%_HP_@T°C_BASIS_CIRCUIT"}
            getValue={(s) =>
              `${Math.round((hasTcoupure ? (s.part_pac_circuits_pac_t_coupure ?? s.part_pac_circuits_pac) : s.part_pac_circuits_pac) * 100)} ${t(
                "FO_UNIT_PERCENT"
              )}`
            }
          />
        );
        const tauxCouvertureCircuitRow = (
          <Row
            caption="FO_RESULT_%_ANNUAL_COV_CIRCUIT"
            getValue={(s) =>
              `${Math.round(s.taux_couverture_circuits_pac * 100)} ${t(
                "FO_UNIT_PERCENT"
              )}`
            }
          />
        );
        result = (
          <>
            {service === ServiceNames.DoubleService ? (
              <>
                {hybridePerformanceRows}
                {annualCircuitDSRow}
              </>
            ) :
              <>

                {puissancePacTotaleTbaseRow}
                {partPacTbaseRows}
                {partPacCircuit}
                {tauxCouverturePACAnnualEstimeRow}
                {tauxCouvertureCircuitRow}
              </>
            }
          </>
        );
        break;
    }
    return result;
  }, [Row, installationType, hasTcoupure]);

  const electricalInfoGridRows = useMemo(() => {
    const puissanceElecMaxRow = (
      <Row
        caption="FO_RESULT_ELEC_OUTPUT_MAX"
        helpText={t("FO_HELP_ELEC_OUTPUT_MAX")}
        getValue={(s) =>
          `${roundTo1Decimals(s.puissance_elec_max)} ${t("FO_UNIT_KW")}`
        }
      />
    );
    const intensiteElecMaxRow = (
      <Row
        caption="FO_RESULT_ELEC_INTENSITY_MAX"
        getValue={(s) => `${s.intensite_elec_max.toFixed(1)} ${t("FO_UNIT_A")}`}
      />
    );
    const puissanceActiveRow = (
      <Row
        caption="FO_RESULT_APPARENT_OUTPUT"
        helpText={t("FO_HELP_APPARENT_OUTPUT")}
        getValue={(s) => `${s.puissance_active.toFixed(1)} ${t("FO_UNIT_KWA")}`}
      />
    );

    let result: ReactElement | null = null;
    switch (installationType) {
      case InstallationType.electric:
      case InstallationType.hybride:
        result = (
          <>
            {puissanceElecMaxRow}
            {intensiteElecMaxRow}
            {puissanceActiveRow}
          </>
        );
        break;
    }
    return result;
  }, [Row, installationType]);

  const hidraulicResultGridRows = useMemo(() => {
    const debitPrimairePacTotalRow = (
      <Row
        caption="FO_RESULT_FLOW_RATE_RECO"
        helpText={t("FO_HELP_FLOW_RATE_RECO")}
        getValue={(s) =>
          `${s.debit_primaire_pac_total.toFixed(1)} ${t("FO_UNIT_M3H")}`
        }
      />
    );
    const volumePrimairePacRow = (
      <Row
        caption="FO_RESULT_PRIMARY_VOLUME"
        helpText={t("FO_HELP_PRIMARY_VOLUME")}
        getValue={(s) =>
          withFxBallonDash(s, () => `${s.pac?.volume_primaire} ${t("FO_UNIT_L")}`)
        }
      />
    );
    // const diametrRaccordementCollecteurRow = (
    //   <Row
    //     caption="FO_RESULT_PIPE_DIAMETER_RECO"
    //     helpText={t("FO_HELP_PIPE_DIAMETER_RECO")}
    //     getValue={(s) =>
    //       `${s.diametre_raccordement_collecteur.replace("_", '"')}`
    //     }
    //   />
    // );
    // const hmtDisponibleRow = (
    //   <Row
    //     caption="FO_RESULT_AVAILABLE_PRESSURE"
    //     getValue={(s) => `${s.hmt_disponible.toFixed(2)} ${t("FO_UNIT_KPA")}`}
    //   />
    // );
    const hmtPdcPartieCommuneRow = (
      <Row
        caption="FO_RESULT_PRECO_HMT"
        helpText={t("FO_HELP_PRESSURE_DROP")}
        getValue={(s) =>
          withFxBallonDash(s, () =>
            s.canalisation?.pdc_partie_commune
              ? `${s.canalisation?.pdc_partie_commune.toFixed()} ${t("FO_UNIT_KPA")}`
              : "-"
          )
        }
      />
    );

    const pressionMaximumDeServiceRow = (
      <Row
        caption="FO_RESULT_SERVICE_PRESSURE"
        getValue={(s) => `${s.pac.pression_max} ${t("FO_UNIT_BAR")}`}
      />
    );

    const flowRateUnitary = (
      <Row
        caption="FO_RESULT_FLOW_RATE_UNITARY"
        getValue={(s) => `${(s.debit_primaire_pac_total / s.pac.nombre).toFixed(1)} ${t("FO_UNIT_M3H")}`}
      />
    );

    const debitPreconiseSerpentin = (
      <Row
        caption="FO_RESULT_FLOW_RATE_SERP"
        getValue={(s) =>
          `${s.ecs?.debit_primaire_preconise?.toFixed(1)} ${t("FO_UNIT_M3H")}`
        }
      />
    );

    let result: ReactElement | null = null;
    switch (installationType) {
      case InstallationType.electric:
      case InstallationType.hybride:
        result = (
          <>
            {debitPrimairePacTotalRow}
            {flowRateUnitary}
            {volumePrimairePacRow}
            {/* {diametrRaccordementCollecteurRow} */}
            {/* {hmtDisponibleRow} */}
            {hmtPdcPartieCommuneRow}
            {pressionMaximumDeServiceRow}
            {(service === ServiceNames.ECS ||
              service === ServiceNames.DoubleService) &&
              json?.systeme != SystemCode.ECHANGEUR_BALLONS_ECS &&
              debitPreconiseSerpentin}
          </>
        );
        break;
    }
    return result;
  }, [Row, installationType, json?.systeme, withFxBallonDash]);

  const installationInfoGridRows = useMemo(() => {
    const surfaceSolDegagementRow = (
      <Row
        caption="FO_RESULT_INSTALL_SURFACE"
        helpText={t("FO_HELP_INSTALL_SURFACE")}
        getValue={(s) =>
          `${s.surface_sol_degagement.toFixed(1)} ${t("FO_UNIT_M2")}`
        }
      />
    );
    const dimensions1PACHLPRow = (
      <Row
        caption="FO_RESULT_DIMENSIONS_1xHP_AxBxC"
        getValue={(s) =>
          `${s.pac.hauteur} x ${s.pac.largeur} x ${s.pac.profondeur} ${t(
            "FO_UNIT_MM"
          )}`
        }
      />
    );
    const hauteurMinimaleLocalRow = (
      <Row
        caption="FO_RESULT_text.hauteur_sous_plafond"
        getValue={(s) => `${s.gros_ballon.hauteur} ${t("FO_UNIT_MM")}`}
      />
    );
    const largeurDePassageMinimaleLocalRow = (
      <Row
        caption="FO_RESULT_text.largeur_de_passage"
        getValue={(s) => `${s.gros_ballon.diametre} ${t("FO_UNIT_MM")}`}
      />
    );

    let result: ReactElement | null = null;
    switch (installationType) {
      case InstallationType.electric:
      case InstallationType.hybride:
        result = (
          <>
            {surfaceSolDegagementRow}
            {dimensions1PACHLPRow}
            {hauteurMinimaleLocalRow}
            {largeurDePassageMinimaleLocalRow}
          </>
        );
        break;
    }
    return result;
  }, [Row, installationType]);

  const acousticInfoGridRows = useMemo(() => {
    const puissanceSonoreChargePartielleRow = (
      <Row
        caption="FO_RESULT_ACOUSTIC_POWER_1xHP_EN12-102"
        helpText={t("FO_HELP_ACOUSTIC_POWER_1xHP_EN12-102")}
        getValue={(s) =>
          `${s.puissance_sonore.charge_partielle} ${t("FO_UNIT_DBA")}`
        }
      />
    );
    const puissanceSonorePleineChargeRow = (
      <Row
        caption="FO_RESULT_ACOUSTIC_POWER_FULL_LOAD"
        helpText={t("FO_HELP_ACOUSTIC_POWER_FULL_LOAD")}
        getValue={(s) =>
          `${s.puissance_sonore.pleine_charge} ${t("FO_UNIT_DBA")}`
        }
      />
    );

    let result: ReactElement | null = null;
    switch (installationType) {
      case InstallationType.electric:
      case InstallationType.hybride:
        result = (
          <>
            {puissanceSonoreChargePartielleRow}
            {puissanceSonorePleineChargeRow}
          </>
        );
        break;
    }
    return result;
  }, [Row, installationType]);

  return (
    <>
      {(isLoading || isLoadingEtudeJsonSave) && <Loader />}
      <view data-vertical="">
        <view data-direction="row">
          {isSolutionsFetching && <Loader />}

          {solutionRequestBody && (
            <SolutionFilters
              type={installationType}
              appliedSolutionFilter={solutionRequestBody}
              service={service}
              hasEcs={hasEcs}
              hasChauffage={hasChauffage}
              hasTcoupure={hasTcoupure}
            />
          )}

          <view
            data-name="switchable"
            data-direction="column"
            data-align="start"
          >
            <view data-container-type="grid">
              <group
                data-align="center"
                data-gap="5"
                data-elevation="1"
                data-index="103"
                data-backdrop=""
                data-wrap="no"
                data-sticky="top"
              >
                <group
                  data-wrap="no"
                  data-direction="column"
                  data-space="10"
                  data-space-left="30"
                >
                  <Text data-text-size="22">FO_RESULT_OUR_SOLUTIONS</Text>
                  <Text>FO_RESULT_DESCRIPTION</Text>
                </group>
              </group>
              <group
                data-space-horizontal="30"
                data-scroll-mask="false"
                data-scroll-padding="20"
                //data-scroll-type="snap"
                data-height="autofit"
                data-scroll=""
              //data-text-size="12"
              >
                {solutions?.length > 0 ? (
                  <Scroll noButton bidirectional>
                    <group
                      data-width="auto"
                      data-scroll=""
                      className={isStuck ? "gridstuck" : ""}
                    >
                      <group data-width="auto">
                        <group
                          data-wrap="no"
                          data-direction="column"
                          data-width="auto"
                        >
                          <group
                            data-position="absolute"
                            data-height="10"
                            data-sticky-row=""
                            data-wrap="no"
                          >
                            <group data-sticky="left" data-length="380">
                              <StickyElement
                                threshold={0.99}
                                callback={setIsStuckCallback}
                              >
                                <group
                                  data-height="100"
                                  style={{
                                    width: "100px",
                                  }}
                                ></group>
                              </StickyElement>
                            </group>
                            <group
                              style={{ width: `${solutions.length * 260}px` }}
                            ></group>
                          </group>
                          {topActionBlocksRow}

                          <Grid
                            title="FO_RESULT_GENERAL_INFO"
                            itemCount={solutions.length}
                            rows={generalInfoGridRows}
                          />
                          <Grid
                            title="FO_RESULT_PERFORMANCE"
                            itemCount={solutions.length}
                            rows={performanceGridRows}
                          />
                          <Grid
                            title="FO_RESULT_ELECTRICAL_INFO"
                            itemCount={solutions.length}
                            rows={electricalInfoGridRows}
                          />
                          <Grid
                            title="FO_RESULT_HYDRAULIC_INFO"
                            itemCount={solutions.length}
                            rows={hidraulicResultGridRows}
                          />
                          <Grid
                            title="FO_RESULT_INSTALLATION_INFO"
                            itemCount={solutions.length}
                            rows={installationInfoGridRows}
                          />
                          <Grid
                            title="FO_RESULT_ACOUSTIC_INFO"
                            itemCount={solutions.length}
                            rows={acousticInfoGridRows}
                          />
                          {/* {bottomActionBlocksRow} */}
                        </group>
                      </group>
                    </group>
                  </Scroll>
                ) : (
                  <EmptyContent
                    text={hasEcs ? "FO_NOSOLUTION_ECS_HP" : "FO_NOSOLUTION_HP"}
                  />
                )}
              </group>
            </view>
          </view>
        </view>
      </view>
      {isAuth && (
        <group
          data-border="overprint"
          data-background="white"
          data-align="center"
          data-gap="10"
          data-space="10"
          data-justify="end"
          data-sticky="bottom"
        >
          <Button
            onClick={async () => {
              isAuth && (await handleUpdateJson());
              !isLoadingEtudeJsonSave &&
                openPopup(
                  <SavePopup
                    closePopup={closeLastPopup}
                    application={json?.application || ""}
                    subApplication={json?.subApplication}
                    systeme={json?.systeme || ""}
                    formData={jsonToSave}
                  ></SavePopup>
                );
            }}
            disabled={!isAuth}
            primary
            large
            text="Sauvegarder"
            icon="save"
          />
        </group>
      )}
    </>
  );
};

interface SolutionRowPropsInner {
  solutions: Solution[];
  caption?: string;
  captionBlock?: ReactNode;
  noCaption?: boolean;
  isTitle?: boolean;
  caption2?: string;
  helpText?: string;
  sticky?: boolean;
  skipCaption?: boolean;
  index?: number;
  borderProp?: Record<string, string>;
  valueHelpText?: string;
  isValueWarning?: (solution: Solution) => boolean;
  textSize?: number | string;
}

interface SolutionRowGetValueProps extends SolutionRowPropsInner {
  getValue: (solution: Solution) => any;
  render?: never;
}

interface SolutionRowRenderProps extends SolutionRowPropsInner {
  getValue?: never;
  render: (solution: Solution) => ReactElement;
}

type SolutionRowProps = SolutionRowGetValueProps | SolutionRowRenderProps;

const SolutionRow: React.FC<SolutionRowProps> = ({
  solutions,
  captionBlock,
  noCaption,
  caption,
  caption2,
  helpText,
  isTitle,
  skipCaption,
  render,
  getValue,
  sticky,
  index,
  borderProp,
  valueHelpText,
  isValueWarning,
  textSize,
}) => (
  <group
    data-wrap="no"
    data-sticky-row=""
    data-background="main-background"
    {...(sticky ? { "data-sticky": "top" } : {})}
    style={{ ...(index ? { zIndex: 102 + index } : {}) }}
  >
    {!noCaption &&
      (captionBlock || skipCaption ? (
        <group
          data-length="380"
          data-background="main-background"
          data-sticky="left"
          {...(borderProp ? borderProp : { "data-border": "inset" })}
        >
          {captionBlock}
        </group>
      ) : (
        <RowCaptionBlock
          text={caption || ""}
          text2={caption2}
          helpText={helpText}
          borderProp={borderProp}
        />
      ))}
    {solutions.map((s) => (
      <Fragment key={getSolutionID(s)}>
        {render ? (
          render(s)
        ) : (
          <ValueBlock
            value={getValue!(s)}
            isTitle={isTitle}
            isWarning={isValueWarning ? isValueWarning(s) : false}
            helpText={isValueWarning ? valueHelpText : undefined}
            textSize={isValueWarning ? isValueWarning(s) ? 15 : textSize : textSize}
          />
        )}
      </Fragment>
    ))}
  </group>
);
